import React, { useState, useEffect } from 'react';
import { Button, Box, Flex, Text } from 'theme-ui';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { FaLink, FaFileAlt } from 'react-icons/fa';
import { bucketUrl } from '../../utils/global';
import { downloadAttachments } from '../../utils/GenerateQueryString';
import { uploadIcon } from '../../helper/uploadType';
import { GdLinksModalProps, PdfLinksModalProps } from './DeliveryHistory.types';

export const GdLinksModal: React.FC<GdLinksModalProps> = ({
  isOpen,
  links,
  linkLabel = 'GD Link',
  onClose,
  isDark,
  modalBg,
  borderColor,
  titleColor,
  bucketUrl: bUrl,
}) => {
  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} toggle={onClose} backdrop="static" keyboard={false} centered size="md">
      <ModalHeader toggle={onClose} style={{ backgroundColor: modalBg, color: titleColor }}>
        {linkLabel}s ({links.length})
      </ModalHeader>
      <ModalBody style={{ backgroundColor: modalBg, color: titleColor, padding: '20px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {links.map((linkUrl, idx) => {
            const fullUrl = linkUrl.startsWith('http') ? linkUrl : `${bUrl}${linkUrl}`;
            return (
              <Flex
                key={idx}
                sx={{
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 14px',
                  borderRadius: '6px',
                  border: `1px solid ${borderColor}`,
                  backgroundColor: isDark ? 'rgba(119, 67, 219, 0.12)' : '#f8fafc',
                }}
              >
                <Flex sx={{ alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
                  <FaLink size={16} color={isDark ? '#c084fc' : '#7743DB'} style={{ flexShrink: 0 }} />
                  <a
                    href={fullUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: isDark ? '#c084fc' : '#7743DB',
                      textDecoration: 'none',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {`${linkLabel} ${idx + 1}`}
                  </a>
                </Flex>
                <a
                  href={fullUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    fontSize: '12px',
                    padding: '4px 12px',
                    backgroundColor: '#7743DB',
                    color: '#ffffff',
                    borderRadius: '4px',
                    textDecoration: 'none',
                    fontWeight: 500,
                  }}
                >
                  Open Link
                </a>
              </Flex>
            );
          })}
        </Box>
      </ModalBody>
    </Modal>
  );
};

export const PdfLinksModal: React.FC<PdfLinksModalProps> = ({
  isOpen,
  links,
  onClose,
  isDark,
  modalBg,
  titleColor,
  bucketUrl: bUrl,
}) => {
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedIndices([]);
      setIsDownloading(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} toggle={onClose} backdrop="static" keyboard={false} centered size="md">
      <ModalHeader toggle={onClose} style={{ backgroundColor: modalBg, color: titleColor }}>
        Attachments ({links.length})
      </ModalHeader>
      <ModalBody style={{ backgroundColor: modalBg, color: titleColor, padding: '20px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Flex sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <Text sx={{ fontWeight: 600, fontSize: '14px', color: titleColor }}>Current documents:</Text>
            {selectedIndices.length > 0 && (
              <Button
                type="button"
                variant="buttons.SaveButton"
                disabled={isDownloading}
                onClick={async () => {
                  const urls = selectedIndices.map(i => links[i]).filter(Boolean);
                  if (!urls.length) return;
                  setIsDownloading(true);
                  try {
                    await downloadAttachments(urls);
                  } catch {
                    /* ignore */
                  } finally {
                    setIsDownloading(false);
                  }
                }}
                sx={{
                  fontSize: '12px',
                  py: '4px',
                  px: '10px',
                  cursor: isDownloading ? 'not-allowed' : 'pointer',
                  opacity: isDownloading ? 0.7 : 1,
                }}
              >
                {isDownloading ? 'Downloading...' : selectedIndices.length === 1 ? 'Download' : 'Download All'}
              </Button>
            )}
          </Flex>

          {selectedIndices.length === 0 && (
            <Text sx={{ fontSize: '13px', color: '#ef4444', fontStyle: 'italic', marginTop: '-4px' }}>
              * Please select checkbox to download attachments
            </Text>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '240px', overflowY: 'auto', padding: '2px' }}>
            {links.map((url, idx) => {
              const parts = url.split('/');
              const filename = parts[parts.length - 1];
              const ext = filename.split('.').pop()?.toLowerCase() || '';
              const iconSrc = uploadIcon(ext);
              const fullUrl = url.startsWith('http') ? url : `${bUrl}${url}`;

              return (
                <Flex
                  key={idx}
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: isDark ? '1px solid #374151' : '1px solid #E2E8F0',
                    backgroundColor: isDark ? 'rgba(13,202,240,0.12)' : 'rgba(248,249,250,1)',
                    gap: '12px',
                  }}
                >
                  <Flex sx={{ alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
                    <input
                      type="checkbox"
                      checked={selectedIndices.includes(idx)}
                      onChange={() => setSelectedIndices(prev => prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx])}
                      style={{ cursor: 'pointer', flexShrink: 0, width: '16px', height: '16px', accentColor: '#7743DB' }}
                    />
                    {iconSrc ? (
                      <img src={iconSrc} alt={ext} style={{ width: '22px', height: '22px', objectFit: 'contain', flexShrink: 0 }} />
                    ) : (
                      <FaFileAlt size={20} color="#0dcaf0" style={{ flexShrink: 0 }} />
                    )}
                    <a
                      href={fullUrl}
                      target="_blank"
                      rel="noreferrer"
                      title={filename}
                      style={{
                        fontSize: '13px',
                        fontWeight: 500,
                        color: '#0dcaf0',
                        textDecoration: 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '280px',
                        display: 'inline-block',
                      }}
                    >
                      {`Link ${idx + 1}`}
                    </a>
                  </Flex>
                </Flex>
              );
            })}
          </Box>

          <Flex sx={{ justifyContent: 'flex-end', pt: 2 }}>
            <Button
              variant="buttons.CancelButton"
              onClick={onClose}
            >
              Cancel
            </Button>
          </Flex>
        </Box>
      </ModalBody>
    </Modal>
  );
};

export interface LinkButtonProps {
  label: string;
  links: string[];
  onMultiClick?: () => void;
  isPdf?: boolean;
  isDark: boolean;
}

const LinkButton: React.FC<LinkButtonProps> = ({
  label,
  links,
  onMultiClick,
  isPdf = false,
  isDark,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const count = links.length;
  const primaryColor = isDark ? '#c084fc' : '#7743DB';
  const modalBg = isDark ? '#1e293b' : '#ffffff';
  const borderColor = isDark ? '#334155' : '#e2e8f0';
  const titleColor = '#38bdf8';

  // wide enough for the longest label (FrontApp) plus the count badge
  const btnWidth = isPdf ? '58px' : '84px';

  const badgeStyle: React.CSSProperties = {
    backgroundColor: '#7743DB',
    color: '#ffffff',
    borderRadius: '50%',
    padding: 0,
    fontSize: '9px',
    fontWeight: 700,
    height: '16px',
    width: '16px',
    // keeps the badge a circle when the label is long (it was squeezed into an oval inside the fixed-width button)
    flexShrink: 0,
    lineHeight: 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const handleMultiClick = () => {
    if (onMultiClick) {
      onMultiClick();
    } else {
      setIsOpen(true);
    }
  };

  if (count === 0) {
    return (
      <Button
        variant="buttons.LinkButton"
        sx={{
          border: `1px solid ${primaryColor}`,
          color: primaryColor,
          cursor: 'not-allowed',
          opacity: 0.5,
          background: 'transparent',
          width: btnWidth,
          minWidth: btnWidth,
          flexShrink: 0,
          padding: '0 6px',
        }}
      >
        {label}
      </Button>
    );
  }

  if (count === 1) {
    const url = links[0].startsWith('http') ? links[0] : `${bucketUrl}${links[0]}`;
    return (
      <Button
        type="button"
        onClick={() => (isPdf ? downloadAttachments([links[0]]) : window.open(url, '_blank', 'noopener,noreferrer'))}
        variant="buttons.LinkButton"
        sx={{
          border: `1px solid ${primaryColor}`,
          color: primaryColor,
          background: 'transparent',
          width: btnWidth,
          minWidth: btnWidth,
          flexShrink: 0,
          padding: '0 6px',
        }}
      >
        {label}{' '}
        <span style={badgeStyle}>
          {count}
        </span>
      </Button>
    );
  }

  return (
    <>
      <Button
        type="button"
        onClick={handleMultiClick}
        variant="buttons.LinkButton"
        sx={{
          border: `1px solid ${primaryColor}`,
          color: primaryColor,
          background: 'transparent',
          width: btnWidth,
          minWidth: btnWidth,
          flexShrink: 0,
          padding: '0 6px',
        }}
      >
        {label}{' '}
        <span style={badgeStyle}>
          {count}
        </span>
      </Button>

      {isPdf ? (
        <PdfLinksModal
          isOpen={isOpen}
          links={links}
          onClose={() => setIsOpen(false)}
          isDark={isDark}
          modalBg={modalBg}
          titleColor={titleColor}
          bucketUrl={bucketUrl}
        />
      ) : (
        <GdLinksModal
          isOpen={isOpen}
          links={links}
          linkLabel={label}
          onClose={() => setIsOpen(false)}
          isDark={isDark}
          modalBg={modalBg}
          borderColor={borderColor}
          titleColor={titleColor}
          bucketUrl={bucketUrl}
        />
      )}
    </>
  );
};

export default LinkButton;
